import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { setIn } from '@aytm/app/insights/dashboard/lib/objects'

import './Data.scss'

import EmptyEditor from './EmptyEditor'
import NumberEditor from './NumberEditor'
import TextEditor from './TextEditor'
import DateEditor from './DateEditor'

const CLASS = 'insights-portal-editor-data'

const PREFIX = 'insights_input'

const Data = props => {
  const className = props.className || CLASS

  const editor = useMemo(() => props.editor || {}, [props.editor])

  const subject = useMemo(() => editor.data || {}, [editor.data])

  const caption = useMemo(() => props.caption || {}, [props.caption])

  const metrics = caption.metrics || null
  const attribute = caption.attribute || ''
  const attributeIndex = props.attributeIndex || 0

  const [value, type] = useMemo(() => {
    const source = metrics ? subject.values?.[attribute]?.metrics?.[metrics.id] : subject.values?.[attribute]
    return source ? [source.value || 0 === source.value ? source.value : '', source.type || ''] : ['', '']
  }, [attribute, metrics, subject])

  const values = useMemo(() => subject.values || null, [subject.values])

  const name = caption.name || attribute

  const placeholder = useMemo(
    () => ('date' === type ? 'MM/DD/YYYY' : metrics ? metrics.name : name),
    [name, metrics, type]
  )

  const unit = `${'percent' === type ? ' %' : ''}`
  const max = 'percent' === type ? 100 : 99999
  const accuracy = ['responses', 'respondents'].includes(attribute) ? 0 : 2

  const change = event => {
    const value = event.value
    props.onCheck?.('title', editor.data?.text)
    if ('percent' === type && (value < 0 || value > 100)) {
      event.preventDefault()
    } else if (props.onEditorChange) {
      const newSubject = Object.assign({}, subject)
      if (metrics) {
        setIn(['values', attribute, 'metrics', metrics.id, 'value'], event.value, newSubject)
      } else {
        setIn(['values', attribute, 'value'], event.value, newSubject)
      }
      props.onEditorChange({ action: 'change', data: newSubject })
    }
  }

  const keyDown = event => {
    const keyCode = event.keyCode
    if ([13, 40].includes(keyCode)) {
      const element = document.querySelector(`#${PREFIX}-${attributeIndex + 1}`)
      if (element) element.focus()
      event.preventDefault()
    } else if ([38].includes(keyCode)) {
      const element = document.querySelector(`#${PREFIX}-${attributeIndex - 1}`)
      if (element) element.focus()
      event.preventDefault()
    }
  }

  if (!type) {
    return 'edit' === props.editor.type ? <EmptyEditor className={`${className}-empty`} /> : null
  } else {
    return 'edit' === props.editor.type ? (
      <div className={className}>
        <div className={`${className}-container`}>
          {'date' === type ? (
            <>
              <DateEditor
                id={`${PREFIX}-${attributeIndex}`}
                key={`editor-${attributeIndex}`}
                value={value}
                minDate={
                  'End date' === attribute && values?.['Start date']?.value ? values?.['Start date']?.value : undefined
                }
                maxDate={
                  'Start date' === attribute && values?.['End date']?.value ? values?.['End date']?.value : undefined
                }
                placeholder={placeholder}
                onKeyDown={keyDown}
                onChange={change}
              />
            </>
          ) : ['percent', 'number'].includes(type) ? (
            <>
              <NumberEditor
                id={`${PREFIX}-${attributeIndex}`}
                key={`editor-${attributeIndex}`}
                value={value}
                type={type}
                max={max}
                accuracy={accuracy}
                placeholder={placeholder}
                onKeyDown={keyDown}
                onChange={change}
              />
              <div key={2} className={`${className}-container-unit`}>
                {unit}
              </div>
            </>
          ) : (
            <TextEditor
              id={`${PREFIX}-${attributeIndex}`}
              key={`editor-${attributeIndex}`}
              value={value}
              placeholder={placeholder}
              onKeyDown={keyDown}
              onChange={change}
            />
          )}
        </div>
      </div>
    ) : null
  }
}

Data.propTypes = {
  className: PropTypes.string,
  editor: PropTypes.object,
  caption: PropTypes.object,
  attributeIndex: PropTypes.number,
  onEditorChange: PropTypes.func,
  onCheck: PropTypes.func
}

Data.defaultProps = {
  type: 'add',
  attributeIndex: 0
}

export default Data
